import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FormInstance, FormItemProps, Input } from 'antd';
import useIsNHS from '@hooks/useIsNHS';
import { validate as validateNHS } from 'nhs-number-validator';
import { css } from '@emotion/react';

const normalizeNHSNumber = (value: any) => {
  return typeof value === 'string' ? value.replace(/\s+/g, '') : value;
};

interface NHSNumberFormItemProps {
  FormItemComponent: React.ComponentType<FormItemProps>;
  form: FormInstance;
  nhsNumberFieldName?: string;
  membershipFieldName?: string;
  disabled?: boolean;
  patientNHSNumber?: string | null;
  showFindLink?: boolean;
  hideValidationError?: boolean;
}

export const NHSNumberFormItem = ({
  FormItemComponent,
  form,
  nhsNumberFieldName = 'nhs_number',
  membershipFieldName = 'mem_no',
  disabled = false,
  patientNHSNumber,
  showFindLink = true,
  hideValidationError = false
}: NHSNumberFormItemProps) => {
  const { t } = useTranslation();
  const isNHS = useIsNHS();

  useEffect(() => {
    if (isNHS) {
      form.setFieldValue(nhsNumberFieldName, form.getFieldValue(membershipFieldName));
    }
    if (patientNHSNumber) {
      form.setFieldValue(nhsNumberFieldName, patientNHSNumber);
    }
  }, [patientNHSNumber]);

  const isFieldDisabled = disabled || isNHS || !!patientNHSNumber;

  const nhsValidationRules = [
    {
      required: true,
      validator: (_: any, value: string) => {
        if (!validateNHS(value)) {
          return Promise.reject(
            hideValidationError ? new Error('') : new Error('Please search online for how to find your NHS number')
          );
        }
        return Promise.resolve();
      }
    }
  ];

  return (
    <>
      <FormItemComponent
        label={t('NHS number')}
        layout="vertical"
        hasFeedback
        name={nhsNumberFieldName}
        validateTrigger={['onBlur', 'onChange']}
        rules={nhsValidationRules}
        normalize={normalizeNHSNumber}
      >
        <Input disabled={isFieldDisabled} data-testid="nhs-number" />
      </FormItemComponent>

      {showFindLink && (
        <a
          href="https://www.nhs.uk/nhs-services/online-services/find-nhs-number/"
          target="_blank"
          rel="noreferrer"
          css={css`
            width: fit-content;
            margin-top: -1em;
            padding: 0.5em 0 1.5em 0;
          `}
        >
          {t("Don't have your NHS number?")}
        </a>
      )}
    </>
  );
};
